import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    BlockNoteViewEditor,
    blockTypeSelectItems,
    ComponentsContext,
    FormattingToolbar,
    getDefaultReactSlashMenuItems,
    getFormattingToolbarItems,
    NestBlockButton,
    SuggestionMenuController,
    UnnestBlockButton,
    useBlockNoteEditor,
    useComponentsContext,
    useCreateBlockNote,
    useDictionary,
    useEditorSelectionChange,
    useEditorState
} from '@blocknote/react'
import { type Block, filterSuggestionItems, formatKeyboardShortcut } from '@blocknote/core'
import { BlockNoteView } from '@blocknote/mantine'
import '@blocknote/react/style.css'
import '@blocknote/mantine/style.css'
import { RiBold, RiItalic, RiStrikethrough, RiUnderline } from 'react-icons/ri'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
    faHorizontalRule,
    faListOl,
    faListUl,
    faRotateLeft,
    faRotateRight,
    faTable
} from '@fortawesome/pro-light-svg-icons'

/* -------------------------------------------------------------------------- */
/* Types & constants                                                          */
/* -------------------------------------------------------------------------- */

type BsTheme = 'light' | 'dark'
type BasicTextStyle = 'bold' | 'italic' | 'underline' | 'strike'
type ListBlockType = 'bulletListItem' | 'numberedListItem'

// Shape of BlockNote's default slash-menu items. `key` exists at runtime but
// is omitted from `DefaultReactSuggestionItem`; that type also degrades under
// our `moduleResolution: "node"` (it imports from the `@blocknote/core/extensions`
// subpath, which needs an exports-map-aware resolution), so we describe the
// items ourselves. Editor generics are invariant and don't line up between
// `useCreateBlockNote`/`useBlockNoteEditor` either, hence the loose editor param
// on `getSlashMenuItemsWithKey` below.
interface SlashMenuItem {
    key: string
    title: string
    onItemClick: () => void
    subtext?: string
    badge?: string
    aliases?: string[]
    group?: string
    icon?: React.JSX.Element
}

interface TextEditorProps {
    value?: string
    onChange?: (html: string) => void
    minHeight?: number
    maxHeight?: number
    className?: string
    placeholder?: string
    footer?: React.ReactNode
    onReachedBottom?: () => void
}

// Default formatting-toolbar items we replace with our own equivalents
// (styles, nest/unnest — re-added in our preferred order), or drop entirely
// (colorStyleButton: no colors, ever — pasted colors are stripped too).
const REPLACED_TOOLBAR_ITEMS: readonly string[] = [
    'boldStyleButton',
    'italicStyleButton',
    'underlineStyleButton',
    'strikeStyleButton',
    'nestBlockButton',
    'unnestBlockButton',
    'colorStyleButton'
]

// The block features we expose. The formatting-toolbar block dropdown and the
// slash menu are kept in feature parity: both offer exactly this set.
// This array also defines the slash-menu order; items that share a group must
// stay adjacent or the group label renders more than once (divider is "Basic
// blocks", so it sits with them rather than in its default spot after Media).
const SLASH_MENU_ITEM_KEYS: readonly string[] = [
    'heading',
    'heading_2',
    'heading_3',
    'heading_4',
    'heading_5',
    'heading_6',
    'paragraph',
    'quote',
    'bullet_list',
    'numbered_list',
    'divider',
    'table'
]

const STYLE_ICONS: Record<BasicTextStyle, React.ComponentType> = {
    bold: RiBold,
    italic: RiItalic,
    underline: RiUnderline,
    strike: RiStrikethrough
}

// Px tolerance so we don't require hitting the exact last pixel to count as "bottom".
const SCROLL_BOTTOM_THRESHOLD = 8

// Focusable controls managed by the toolbar's roving tabindex.
const TOOLBAR_BUTTON_SELECTOR = 'button:not(:disabled)'

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const TextEditor: React.FC<TextEditorProps> = ({
    value,
    onChange,
    minHeight = 200,
    maxHeight,
    className,
    placeholder,
    footer,
    onReachedBottom
}) => {
    const theme = useBsTheme()

    // `placeholders` is deprecated in BlockNote but still the supported way to
    // set placeholder copy until the replacement API lands.
    const editor = useCreateBlockNote({
        placeholders: { default: placeholder, emptyDocument: placeholder },
        // Strip colors from pasted HTML (Word, Google Docs, web pages) BEFORE
        // BlockNote parses it, so the pasted content renders theme-appropriate
        // in the editor rather than only being cleaned on output.
        pasteHandler: ({ event, editor, defaultPasteHandler }) => {
            const html = event.clipboardData?.getData('text/html')
            if (html) {
                editor.pasteHTML(normalizeEditorContent(html))
                return true
            }
            return defaultPasteHandler()
        },
        links: {
            // Clicking link text should only move the caret — BlockNote's
            // default opens the URL, which fights with editing it. Deliberate
            // navigation is the link toolbar's open button, plus the usual
            // ctrl/cmd-click affordance. Supplying any `onClick` disables the
            // built-in open-on-click; returning false hands the event back to
            // ProseMirror for normal caret placement.
            onClick: (event: MouseEvent) => {
                if (!event.metaKey && !event.ctrlKey) return false

                // The click can land on an element nested inside the anchor.
                const href = (event.target as Element | null)
                    ?.closest('a[href]')
                    ?.getAttribute('href')
                if (href) window.open(href, '_blank', 'noopener,noreferrer')
                return true
            }
        }
    } as Parameters<typeof useCreateBlockNote>[0])

    useHtmlContent(editor, value)

    // Trim the default dropdown items to our approved block set: drop the
    // toggle-heading variants (isToggleable) and the toggle/check lists.
    // Table and divider are insert-only (converting a text block to them would
    // discard its content), so they get toolbar buttons instead.
    const blockSelectItems = useMemo(
        () =>
            blockTypeSelectItems(editor.dictionary).filter(
                item =>
                    !item.props?.isToggleable &&
                    item.type !== 'toggleListItem' &&
                    item.type !== 'checkListItem'
            ),
        [editor]
    )

    const getSlashMenuItems = useCallback(
        async (query: string) =>
            filterSuggestionItems(
                getSlashMenuItemsWithKey(editor)
                    .filter(item => SLASH_MENU_ITEM_KEYS.includes(item.key))
                    .sort(
                        (a, b) =>
                            SLASH_MENU_ITEM_KEYS.indexOf(a.key) -
                            SLASH_MENU_ITEM_KEYS.indexOf(b.key)
                    )
                    .map(item => ({ ...item, group: slashMenuGroup(item) })),
                query
            ),
        [editor]
    )

    const handleChange = useCallback(() => {
        onChange?.(normalizeEditorContent(editor.blocksToHTMLLossy(editor.document)))
    }, [editor, onChange])

    // When a table is the last block, BlockNote only offers a clickable
    // "trailing block" element below it — keyboard users have no way past the
    // table (ArrowDown at the last row does nothing; ProseMirror finds no valid
    // position after it). Mirror the click behavior: ArrowDown from the end of
    // a cell in the last row of a document-final table creates a paragraph
    // below the table and moves the caret into it.
    const handleEditorKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            // defaultPrevented = ProseMirror already handled it (e.g. moved
            // between table rows), so there's nothing for us to do.
            if (e.key !== 'ArrowDown' || e.defaultPrevented) return

            const { selection } = editor.prosemirrorState
            const { $head } = selection
            // Only for a caret at the very end of its cell's text — mid-text
            // (including wrapped lines) stays with the native caret behavior.
            if (!selection.empty || $head.parentOffset !== $head.parent.content.size) return

            // Must be inside the last row of a table.
            let rowDepth = 0
            for (let d = $head.depth; d > 0; d--) {
                if ($head.node(d).type.name === 'tableRow') {
                    rowDepth = d
                    break
                }
            }
            if (!rowDepth) return
            if ($head.index(rowDepth - 1) !== $head.node(rowDepth - 1).childCount - 1) return

            try {
                const pos = editor.getTextCursorPosition()
                // Top-level trailing table only — anywhere else ArrowDown has
                // real content to move to.
                if (pos.block.type !== 'table' || pos.nextBlock || pos.parentBlock) return

                const [paragraph] = editor.insertBlocks([{ type: 'paragraph' }], pos.block, 'after')
                editor.setTextCursorPosition(paragraph, 'start')
                e.preventDefault()
            } catch {
                // Selection state we can't resolve (e.g. gap cursor) — leave
                // the key press to the editor.
            }
        },
        [editor]
    )

    const scrollRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    // Floating UI splits into two groups. The slash menu caps its height to the
    // space inside its clipping ancestors (floating-ui `size()`), so within our
    // scroll area it collapses to a sliver — it needs document.body, and it's
    // safe there because it never takes DOM focus. The link toolbar and table
    // handles do take focus ("Edit link" renders its inputs inline inside the
    // toolbar), and at body they'd sit outside the focus trap of the Modal the
    // editor lives in, which makes them unfocusable and unreachable by Tab.
    // So: anything that takes DOM focus goes on `floatingHost`, the rest at body.
    const [floatingHost, setFloatingHost] = useState<HTMLDivElement | null>(null)

    // `?? undefined` (not null): before the host mounts, inherit `default`.
    const portalElements = useMemo(
        () => ({
            default: null,
            linkToolbar: floatingHost ?? undefined,
            tableHandles: floatingHost ?? undefined
        }),
        [floatingHost]
    )

    const notifyIfAtBottom = useCallback(() => {
        const el = scrollRef.current
        if (!el || !onReachedBottom) return
        // Also true when content fits (not scrollable), so a short document
        // doesn't leave the consumer waiting for a scroll that never happens.
        if (el.scrollHeight - el.clientHeight - el.scrollTop <= SCROLL_BOTTOM_THRESHOLD) {
            onReachedBottom()
        }
    }, [onReachedBottom])

    // Re-check when the content's height settles (async HTML parse) or changes.
    useEffect(() => {
        const contentEl = contentRef.current
        if (!contentEl || !onReachedBottom) return

        const observer = new ResizeObserver(() => notifyIfAtBottom())
        observer.observe(contentEl)

        return () => observer.disconnect()
    }, [onReachedBottom, notifyIfAtBottom])

    return (
        <div
            className={`text-editor border rounded overflow-hidden${className ? ` ${className}` : ''}`}
            style={{ '--text-editor-min-height': `${minHeight}px` } as React.CSSProperties}
        >
            <BlockNoteView
                editor={editor}
                theme={theme}
                formattingToolbar={false}
                slashMenu={false}
                sideMenu={false}
                filePanel={false}
                emojiPicker={false}
                renderEditor={false}
                onChange={handleChange}
                portalElements={portalElements}
            >
                <div
                    className="text-editor-toolbar border-bottom"
                    onMouseDown={preventToolbarBlur}
                >
                    <StaticToolbarComponents>
                        <FormattingToolbar>
                            <HistoryButton key="undoButton" action="undo" />
                            <HistoryButton key="redoButton" action="redo" />
                            <HideOnGapCursor key="defaultItems">
                                {getFormattingToolbarItems(blockSelectItems).filter(
                                    item => !REPLACED_TOOLBAR_ITEMS.includes(item.key as string)
                                )}
                            </HideOnGapCursor>
                            <StyleButton key="boldButton" style="bold" />
                            <StyleButton key="italicButton" style="italic" />
                            <StyleButton key="underlineButton" style="underline" />
                            <StyleButton key="strikeButton" style="strike" />
                            <ListButton
                                key="bulletListButton"
                                type="bulletListItem"
                                tooltip="Bullet List"
                                icon={faListUl}
                            />
                            <ListButton
                                key="numberedListButton"
                                type="numberedListItem"
                                tooltip="Numbered List"
                                icon={faListOl}
                            />
                            <UnnestBlockButton key="unnestBlockButton" />
                            <NestBlockButton key="nestBlockButton" />
                            <InsertBlockButton key="tableButton" itemKey="table" icon={faTable} />
                            <InsertBlockButton
                                key="dividerButton"
                                itemKey="divider"
                                icon={faHorizontalRule}
                            />
                        </FormattingToolbar>
                    </StaticToolbarComponents>
                </div>
                <div
                    ref={scrollRef}
                    className="text-editor-scroll"
                    style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
                    onScroll={onReachedBottom ? notifyIfAtBottom : undefined}
                    onKeyDown={handleEditorKeyDown}
                >
                    <div ref={contentRef}>
                        <BlockNoteViewEditor />
                        {footer}
                    </div>
                </div>
                {/* Inside BlockNoteView for bn-root/bn-mantine theming, outside
                    the scroll area so it can't be clipped. */}
                <div ref={setFloatingHost} />
                {/* slashMenu={false} disables only the default menu; this one
                    offers the same filtered block set as the toolbar. */}
                <SuggestionMenuController
                    triggerCharacter="/"
                    getItems={getSlashMenuItems}
                />
            </BlockNoteView>
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// Pasted content carries the source document's background/text color (BlockNote
// imports the clipboard's HTML flavor; Word/Google Docs hard-code colors). Strip
// presentational color so content adapts to the app theme (transparent background,
// inherited text color) instead of hard-coding it.
const normalizeEditorContent = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html')

    doc.querySelectorAll('[style]').forEach(el => {
        const cleaned = (el.getAttribute('style') || '')
            .split(';')
            .filter(d => !/^\s*(background(-color)?|color|mso-highlight)\s*:/i.test(d))
            .join(';')
            .trim()
        cleaned ? el.setAttribute('style', cleaned) : el.removeAttribute('style')
    })

    // Legacy color attributes (Word emits <font color> / bgcolor on table cells).
    doc.querySelectorAll('[color],[bgcolor]').forEach(el => {
        el.removeAttribute('color')
        el.removeAttribute('bgcolor')
    })

    // Remove the data-* attributes BlockNote uses to rehydrate color on parse.
    doc.querySelectorAll('[data-background-color],[data-text-color],[data-style-type],[data-value],[data-editable]')
        .forEach(el => ['data-background-color', 'data-text-color', 'data-style-type', 'data-value', 'data-editable']
            .forEach(attr => el.removeAttribute(attr)))

    return doc.body.innerHTML
}

// See the SlashMenuItem interface for why this cast exists.
const getSlashMenuItemsWithKey = (editor: unknown): SlashMenuItem[] =>
    getDefaultReactSlashMenuItems(
        editor as Parameters<typeof getDefaultReactSlashMenuItems>[0]
    ) as unknown as SlashMenuItem[]

// BlockNote's defaults split the heading levels across "Headings" (1-3) and
// "Subheadings" (4-6); merge them under a single "Headings" label.
const slashMenuGroup = (item: SlashMenuItem): string | undefined =>
    item.key.startsWith('heading') ? 'Headings' : item.group

const getBsTheme = (): BsTheme =>
    document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light'

const preventToolbarBlur: React.MouseEventHandler = e => {
    if (!(e.target as HTMLElement).closest('input, textarea, [contenteditable]')) {
        e.preventDefault()
    }
}

/** Strips HTML tags and returns the visible text content (spaces included). */
const htmlToPlainText = (html: string): string =>
    new DOMParser().parseFromString(html || '', 'text/html').body.textContent || ''

/* -------------------------------------------------------------------------- */
/* Hooks                                                                      */
/* -------------------------------------------------------------------------- */

/** Tracks Bootstrap's `data-bs-theme` and re-renders on change. */
const useBsTheme = (): BsTheme => {
    const [theme, setTheme] = useState<BsTheme>(getBsTheme)

    useEffect(() => {
        const observer = new MutationObserver(() => setTheme(getBsTheme()))
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-bs-theme']
        })
        return () => observer.disconnect()
    }, [])

    return theme
}

/**
 * Seeds the editor's HTML once on mount so the user's edits aren't clobbered.
 *
 * `tryParseHTMLToBlocks` is async in recent BlockNote versions, so it's
 * awaited defensively; `cancelled` guards against out-of-order resolutions.
 */
const useHtmlContent = (
    editor: ReturnType<typeof useCreateBlockNote>,
    value: string | undefined
): void => {
    const seededRef = useRef(false)

    useEffect(() => {
        if (seededRef.current) return
        seededRef.current = true

        let cancelled = false
        Promise.resolve(value ? editor.tryParseHTMLToBlocks(value) : []).then(blocks => {
            if (!cancelled) editor.replaceBlocks(editor.document, blocks as Block[])
        })

        return () => {
            cancelled = true
        }
    }, [editor, value])
}

/** True when the cursor's block matches `type`; updates on selection change. */
const useBlockTypeActive = (
    editor: ReturnType<typeof useBlockNoteEditor>,
    type: ListBlockType
): boolean => {
    const [isActive, setIsActive] = useState(false)

    useEditorSelectionChange(() => {
        const pos = editor.getTextCursorPosition()
        setIsActive(pos?.block.type === type)
    })

    return isActive
}

/* -------------------------------------------------------------------------- */
/* Toolbar components                                                         */
/* -------------------------------------------------------------------------- */

/**
 * BlockNote's Mantine toolbar engages a focus trap whenever focus lands inside
 * it (Mantine `useFocusTrap`: yanks focus to the first button and scopes Tab
 * to the toolbar via a document-level handler). That's right for its floating
 * selection toolbar, but ours is a static page element — trapping turns it
 * into a keyboard cage (WCAG 2.1.2, "No Keyboard Trap").
 *
 * Instead, implement the ARIA toolbar pattern (roving tabindex): the toolbar
 * is a single Tab stop — Tab enters it, Tab again leaves it — and Left/Right
 * (plus Home/End) move between buttons.
 */
const StaticToolbarRoot = forwardRef<
    HTMLDivElement,
    {
        className?: string
        children?: React.ReactNode
        onMouseEnter?: React.MouseEventHandler
        onMouseLeave?: React.MouseEventHandler
        variant?: string
    }
>(({ className, children, onMouseEnter, onMouseLeave }, ref) => {
    const rootRef = useRef<HTMLDivElement | null>(null)

    const setRefs = useCallback(
        (node: HTMLDivElement | null) => {
            rootRef.current = node
            if (typeof ref === 'function') ref(node)
            else if (ref) ref.current = node
        },
        [ref]
    )

    const getButtons = () =>
        Array.from(rootRef.current?.querySelectorAll<HTMLElement>(TOOLBAR_BUTTON_SELECTOR) ?? [])

    // Roving tabindex: exactly one button stays in the page tab order.
    const setTabStop = useCallback((target?: Element | null) => {
        const buttons = getButtons()
        const current =
            target instanceof HTMLElement && buttons.includes(target) ? target : buttons[0]
        buttons.forEach(b => b.setAttribute('tabindex', b === current ? '0' : '-1'))
    }, [])

    // Initialize, and re-apply when buttons mount/unmount or get disabled
    // (HideOnGapCursor, the block dropdown hiding itself inside tables,
    // nest/unnest disabled states) so the toolbar always keeps one tab stop.
    useEffect(() => {
        const el = rootRef.current
        if (!el) return undefined

        const reapply = () => setTabStop(rootRef.current?.querySelector('[tabindex="0"]'))
        reapply()

        // `attributeFilter` means our own tabindex writes don't re-trigger it.
        const observer = new MutationObserver(reapply)
        observer.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] })
        return () => observer.disconnect()
    }, [setTabStop])

    // The most recently focused button becomes the toolbar's Tab return stop.
    const handleFocus: React.FocusEventHandler = e => {
        if (e.target instanceof HTMLElement && e.target.matches(TOOLBAR_BUTTON_SELECTOR)) {
            setTabStop(e.target)
        }
    }

    const handleKeyDown: React.KeyboardEventHandler = e => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return

        const buttons = getButtons()
        const index = buttons.indexOf(document.activeElement as HTMLElement)
        if (index === -1) return

        const next =
            e.key === 'ArrowLeft'
                ? buttons[(index - 1 + buttons.length) % buttons.length]
                : e.key === 'ArrowRight'
                    ? buttons[(index + 1) % buttons.length]
                    : e.key === 'Home'
                        ? buttons[0]
                        : buttons[buttons.length - 1]

        next?.focus()
        setTabStop(next)
        e.preventDefault()
    }

    return (
        <div
            ref={setRefs}
            className={className}
            role="toolbar"
            aria-label="Text formatting"
            style={{ display: 'flex' }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
        >
            {children}
        </div>
    )
})
StaticToolbarRoot.displayName = 'StaticToolbarRoot'

/** Provides the components context with the trap-free toolbar root. */
const StaticToolbarComponents: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const components = useComponentsContext()!

    const value = useMemo(
        () =>
            ({
                ...components,
                FormattingToolbar: {
                    ...components.FormattingToolbar,
                    Root: StaticToolbarRoot
                }
            }) as typeof components,
        [components]
    )

    return <ComponentsContext.Provider value={value}>{children}</ComponentsContext.Provider>
}

/**
 * BlockNote's default toolbar components assume a text or cell selection.
 * With a gap cursor (the horizontal caret shown at a document edge next to a
 * table), TextAlignButton's selector calls the table extension's
 * getCellSelection(), whose position arithmetic walks out of the document
 * (`resolve(-1)`) and throws, crashing the page. Stock BlockNote never hits
 * this because its floating toolbar only mounts over text/cell selections —
 * our toolbar is static, so hide the selection-dependent default items while
 * a gap cursor is active.
 */
const HideOnGapCursor: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const editor = useBlockNoteEditor()

    const isGapCursor = useEditorState({
        editor,
        selector: ({ editor }) =>
            // `_tiptapEditor` is BlockNote-internal; same access pattern as
            // StyleButton below.
            editor ? editor._tiptapEditor.state.selection.toJSON().type === 'gapcursor' : false
    })

    if (isGapCursor) return null

    return <>{children}</>
}

const HistoryButton: React.FC<{ action: 'undo' | 'redo' }> = ({ action }) => {
    const editor = useBlockNoteEditor()
    const components = useComponentsContext()!

    const { tooltip, icon } =
        action === 'undo'
            ? { tooltip: 'Undo', icon: faRotateLeft }
            : { tooltip: 'Redo', icon: faRotateRight }

    return (
        <components.Generic.Toolbar.Button
            mainTooltip={tooltip}
            label={tooltip}
            onClick={() => editor[action]()}
            icon={<FontAwesomeIcon icon={icon} />}
        />
    )
}

const ListButton: React.FC<{
    type: ListBlockType
    tooltip: string
    icon: IconDefinition
}> = ({ type, tooltip, icon }) => {
    const editor = useBlockNoteEditor()
    const components = useComponentsContext()
    const isActive = useBlockTypeActive(editor as any, type)

    return (
        <components.Generic.Toolbar.Button
            mainTooltip={tooltip}
            label={tooltip}
            isSelected={isActive}
            onClick={() => {
                const pos = editor.getTextCursorPosition()
                if (!pos) return
                editor.updateBlock(pos.block, {
                    type: pos.block.type === type ? 'paragraph' : type
                })
            }}
            icon={<FontAwesomeIcon icon={icon} />}
        />
    )
}

/**
 * Toolbar button for insert-only blocks (table, divider). Reuses the block's
 * default slash-menu action so toolbar and slash-menu inserts behave
 * identically (e.g. a table arrives with its starter rows).
 */
const InsertBlockButton: React.FC<{
    itemKey: string
    icon: IconDefinition
}> = ({ itemKey, icon }) => {
    const editor = useBlockNoteEditor()
    const components = useComponentsContext()!

    const item = useMemo(
        () => getSlashMenuItemsWithKey(editor).find(i => i.key === itemKey),
        [editor, itemKey]
    )

    if (!item) return null

    return (
        <components.Generic.Toolbar.Button
            mainTooltip={item.title}
            label={item.title}
            onClick={() => {
                editor.focus()
                item.onItemClick()
            }}
            icon={<FontAwesomeIcon icon={icon} />}
        />
    )
}

const StyleButton: React.FC<{ style: BasicTextStyle }> = ({ style }) => {
    const editor = useBlockNoteEditor()
    const components = useComponentsContext()!
    const dict = useDictionary()

    const active = useEditorState({
        editor,
        selector: ({ editor }) => {
            if (!editor) return false
            // `_tiptapEditor` is BlockNote-internal; revisit when a public
            // "is style active" selector becomes available.
            const { storedMarks, selection } = editor._tiptapEditor.state
            const marks = storedMarks ?? selection.$to.marks()
            return marks.some(m => m.type.name === style)
        }
    })

    const Icon = STYLE_ICONS[style]
    const labels = dict.formatting_toolbar[style]

    return (
        <components.FormattingToolbar.Button
            className="bn-button"
            data-test={style}
            isSelected={active}
            label={labels.tooltip}
            mainTooltip={labels.tooltip}
            secondaryTooltip={formatKeyboardShortcut(
                labels.secondary_tooltip,
                dict.generic.ctrl_shortcut
            )}
            icon={<Icon />}
            onClick={() => {
                editor.focus()
                editor.toggleStyles({ [style]: true } as Partial<Record<BasicTextStyle, true>>)
            }}
        />
    )
}

TextEditor.displayName = 'TextEditor'
export { TextEditor, htmlToPlainText }
export type { TextEditorProps }
